import '../../../styles/pages.css'

import { Box, Button } from '@mui/material'
import React, { useEffect, useState } from 'react'

import AddCurriculum from '../shared/AddCurriculum'
import Alert from '@mui/lab/Alert'
import { GetSchoolCurricula } from '../shared/GetCurricula'
import { SchoolFeatures } from '../school_components/SchoolFeatures'
import Snackbar from '@mui/material/Snackbar'
import { getAPIRequest } from 'infrastructure/api'
import DOMPurify from 'dompurify'
import { useSchoolResource } from 'shared/api/school'
import { useDemoResource } from 'shared/api/demo'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { schoolUserCountsWithDefaultAtom } from 'shared/api/school'
import CloneDemo from './CloneDemo'
import { PlainLink } from 'app/components/RouteLink'
import ReinstateResourceButton from '../shared/ReinstateResourceButton'

export function DemoEnvironmentShow({ id }: { id: string }) {
  const { data: demoEnvironment, reFetchDemo } = useDemoResource(id)
  const schoolResource = useSchoolResource(demoEnvironment?.school_id)
  const [demoUrl, setDemoUrl] = useState('')
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const { teacher_count } = useAtomValue(schoolUserCountsWithDefaultAtom)

  useEffect(() => {
    ;(async () => {
      try {
        if (!demoEnvironment) return
        const res = await getAPIRequest(
          `/v1/demo/geturl?demo_uuid=${demoEnvironment.uuid}`,
        )
        if (typeof res === 'string') {
          setDemoUrl(res)
        }
      } catch {}
    })()
  }, [demoEnvironment?.id])

  const reFetchData = () => {
    reFetchDemo()
    schoolResource.reFetchSchool()
  }

  const sanitizedMessage = DOMPurify.sanitize(demoEnvironment?.message ?? '')

  if (!demoEnvironment || !schoolResource.data) {
    return <div>Loading...</div>
  }

  function copyDemoUrl() {
    navigator.clipboard.writeText(demoUrl).then(() => {
      setSnackbarMessage('Demo URL copied to clipboard')
    })
  }

  const dateCreated = new Date(demoEnvironment.date_created).toLocaleString()
  const expirationDate = new Date(
    demoEnvironment.expiration_date,
  ).toLocaleString()
  const demoSchool = schoolResource.data

  const demoType = demoEnvironment.demo_type

  return (
    <div className="content-color">
      <p>
        Demo School: <b>{demoEnvironment.school_name}</b>
      </p>
      <p>
        Demo Type: <b>{demoType}</b>
      </p>
      <p>
        Account length in Days:{' '}
        <b>{demoEnvironment.token_duration_days}</b>{' '}
      </p>
      <p>
        Demo Url: <button onClick={copyDemoUrl}>Copy Demo URL</button>
      </p>
      <p>
        Date Created: <b>{dateCreated}</b>
      </p>
      <p>
        Expiration Date: <b>{expirationDate}</b>
      </p>
      {demoEnvironment.date_deleted && (
        <p>
          Deleted At: <b> {demoEnvironment.date_deleted} </b>
        </p>
      )}
      <p>
        School ID:{' '}
        <PlainLink to={`/schools/${demoEnvironment.school_id}/show`}>
          {demoEnvironment.school_id}
        </PlainLink>
      </p>
      <hr />
      <p>Welcome Message:</p>
      <div dangerouslySetInnerHTML={{ __html: sanitizedMessage }} />
      <hr />
      <div>
        <div className="section-buttons">
          <Button
            variant="outlined"
            component={PlainLink}
            to={`/demos/${demoEnvironment.id}/edit`}
          >
            Edit Demo
          </Button>
          {demoEnvironment.date_deleted && (
            <ReinstateResourceButton
              name={demoEnvironment.school_name}
              resource="Demo"
              demoID={demoEnvironment.id}
            />
          )}
        </div>
        <div className="section-buttons">
          <CloneDemo
            demoID={demoEnvironment.id}
            schoolID={Number(demoEnvironment.school_id)}
          />
        </div>
      </div>
      <hr />
      <div className="section-buttons">
        <span>Total Teachers: {teacher_count}</span>
      </div>
      <div>
        <Box
          sx={{ display: 'flex' }}
          width={'100%'}
          style={{ overflowY: 'auto', maxHeight: '300px' }}
        >
          <GetSchoolCurricula
            schoolId={demoEnvironment.school_id}
            bundledCurricula={demoSchool.school_courses}
          />
        </Box>
        <hr />
        <Box width={'100%'}>
          <Box sx={{ display: 'flex', marginTop: '2%' }}>
            <AddCurriculum
              schoolID={Number(demoEnvironment.school_id)}
              onAdded={reFetchData}
            />
          </Box>
        </Box>
      </div>
      <hr />
      <SchoolFeatures />
      <Snackbar
        open={snackbarMessage?.length > 0}
        onClose={() => setSnackbarMessage('')}
        autoHideDuration={4000}
      >
        <Alert severity="success">{snackbarMessage}</Alert>
      </Snackbar>
    </div>
  )
}
